// Поповер с подробностями о поездке: дата создания, дата правки, роль и
// состав соавторов, статус публичной ссылки.
//
// Раньше всё это лежало прямо в строке списка бейджами и второй строкой дат —
// на телефоне это была самая тяжёлая часть плитки. Вынесено в отдельный
// компонент (а не просто в JSX внутри TripRow): плитка и так плотная, а тут
// ещё своя логика открытия/закрытия и клик-мимо/Esc.
//
// Клик мимо и Esc закрывают поповер тем же приёмом, что и попап кнопки
// аккаунта — см. AccountButton.tsx, он эталон этого поведения в проекте.
import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import { useI18n } from '../i18n';
import type { TripMeta } from './api';
import { formatTripDateFull } from './tripFormat';
import styles from './TripInfoPopover.module.css';

function InfoIcon(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5.5" />
      <path d="M12 7.8v.01" />
    </svg>
  );
}

export function TripInfoPopover({ trip }: { trip: TripMeta }): ReactElement {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const headingId = useId();

  const title = trip.name.trim() || t('trips.untitled');
  const isOwner = trip.role === 'owner';

  // Закрыть и вернуть фокус на кнопку — путь для Esc. Клик мимо (см. onDown
  // ниже) через него не идёт: фокус там и так уже встал туда, куда кликнули.
  function close(): void {
    setOpen(false);
    triggerRef.current?.focus();
  }

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent): void {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div className={styles.box} ref={boxRef}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-label={t('trips.info.button', { name: title })}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <InfoIcon />
      </button>

      {open && (
        <div
          className={styles.panel}
          role="dialog"
          aria-modal="false"
          aria-labelledby={headingId}
        >
          <h3 id={headingId} className={styles.heading}>
            {t('trips.info.title', { name: title })}
          </h3>
          <p>{t('trips.created', { date: formatTripDateFull(trip.createdAt, lang) })}</p>
          <p>{t('trips.updated', { date: formatTripDateFull(trip.updatedAt, lang) })}</p>
          {/* Роль — своей строкой; у соавтора рядом чья это поездка, у
              владельца дополнять нечем. */}
          <p>
            {t(isOwner ? 'trips.access.role.owner' : 'trips.access.role.editor')}
            {!isOwner && ` · ${t('trips.byOwner', { name: trip.ownerName.trim() || trip.ownerEmail })}`}
          </p>
          <p>{t('trips.members', { count: trip.members })}</p>
          <p>{trip.shareId ? t('trips.shared') : t('trips.linkClosed')}</p>
        </div>
      )}
    </div>
  );
}
